import { createAppSlice } from "../app/createAppSlice";
import { createSelector } from "@reduxjs/toolkit";
import { AppDispatch, IRootState } from "../app/store";
import {
  GetMcbimSpacesApiArg,
  openapi,
  PostMcbimItemsApiArg,
  PostMcbimItemsApiResponse,
} from "../../openapi";
import { IFC_CLASSES_CEILING_DEVICES, IfcClassesEnum } from "@multiconsult-group/multiplacer-core";
import { IRoom } from "@multiconsult-group/multiplacer-core";

export interface IMcBimSliceState {
  test: string[];
}

const initialState: IMcBimSliceState = {
  test: [],
};

// If you are not using async thunks you can use the standalone `createSlice`.
export const McBimSlice = createAppSlice({
  name: "mcbim",
  initialState,
  reducers: () => ({}),
});

const selectFloorAndJob = (state: IRootState) => state.design.floorAndJob;
const selectProjectNo = (state: IRootState) => state.design.projectNo;
export const selectGetMcBimSpacesParams = createSelector(
  [selectFloorAndJob, selectProjectNo],
  (floorAndJob, projectNo): GetMcbimSpacesApiArg | null => {
    if (floorAndJob == null || floorAndJob.floorGlobalId == null) {
      return null;
    }
    return {
      projectNo: projectNo,
      floorGlobalId: floorAndJob.floorGlobalId as string,
      jobId: floorAndJob.jobId as string,
    };
  }
);

export const selectedRoomNr = (state: IRootState) => state.design.selectedSpaceNumber;

export const selectArgsHttpMcBimSpaceStandardRoom = createSelector(
  [selectedRoomNr, selectProjectNo, selectFloorAndJob],
  (roomNr, projectNo, floorAndJob) => {
    if (!roomNr || !projectNo || !floorAndJob) return {};
    return {
      projectNo,
      floorGlobalId: floorAndJob.floorGlobalId,
      jobId: floorAndJob.jobId,
    };
  }
);

export const selectHttpMcBimItemsInActiveStandardRoom = createSelector(
  [selectArgsHttpMcBimSpaceStandardRoom, (state: IRootState) => state],
  (args, state) => {
    if (!args.projectNo || !args.floorGlobalId || !args.jobId) return [];
    const resp = openapi.endpoints.getMcbimItemsInSpace.select(args)(state).data;
    return resp ?? [];
  }
);

const selectIfcClassesToDisplay = (state: IRootState) => state.design.ifcClassesToDisplay;

// Selected IFC classes, independent of opacity.
const selectActiveInstanceClasses = createSelector(
  [selectIfcClassesToDisplay],
  (instanceClasses): IfcClassesEnum[] => {
    if (!instanceClasses) return [];

    return instanceClasses.map(entry => entry.ifcClass as IfcClassesEnum);
  }
);

// /** Hämtar argumenten som ska användas för att hämta McBimItems, eller null om vi inte ska hämta */
// export const selectGetMcBimItemsArkArgs = createSelector(
//   [selectActiveInstanceClasses, selectProjectNo, selectFloorAndJob],
//   (activeInstanceClasses, projectNo, fileFloorJob): PostMcbimItemsApiArg | null => {
//     if (!projectNo || !fileFloorJob?.floorName) return null;
//     if (activeInstanceClasses.length === 0) return null; // inget att hämta på

//     return {
//       body: {
//         projectNo,
//         buildingStoryName: fileFloorJob.floorName,
//         instanceClasses: activeInstanceClasses,
//       },
//     };
//   }
// );

const selectDeviceViewMode = (state: IRootState) => state.design.deviceViewMode;
export const selectGetMcBimCeilingDevicesArgs = createSelector(
  [selectProjectNo, selectFloorAndJob, selectDeviceViewMode],
  (projectNo, fileFloorJob, deviceViewMode): PostMcbimItemsApiArg | null => {
    if (!projectNo || !fileFloorJob?.floorName || deviceViewMode === "devices") return null;
    return {
      body: {
        projectNo,
        buildingStoryName: fileFloorJob.floorName,
        instanceClasses: [...IFC_CLASSES_CEILING_DEVICES],
      },
    };
  }
);

export const selectProjectNoAndFloorName = createSelector(
  [selectProjectNo, selectFloorAndJob],
  (projectNo, fileFloorJob): { projectNo: number; buildingStoryName: string } | null => {
    if (!projectNo || !fileFloorJob?.floorName) return null;
    return {
      projectNo,
      buildingStoryName: fileFloorJob.floorName,
    };
  }
);

const enhancedApi = openapi.enhanceEndpoints({
  endpoints: {
    getMcbimSpaces: {
      providesTags: ["mcbim"],
    },
    getMultiplacerProjects: {
      providesTags: ["projects"],
    },
    getMultiplacerProjectsSingleProject: {
      providesTags: ["projects"],
    },
    postMultiplacerProjectEditProject: {
      invalidatesTags: ["projects"],
    },
    putMultiplacerRoomLock: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        await patchRoomFromQueryFulfilled({ dispatch, getState, queryFulfilled });
      },
      invalidatesTags: ["similarRoomsWithDesign"],
    },
    postMultiplacerRooms: {
      providesTags: ["Rooms"],
    },
    postMultiplacerAirflowsImport: {
      invalidatesTags: ["Rooms"],
    },
  },
});

// Typa ett enskilt objekt från GetMcbimSpacesApiResponse och lägg till searchHit och fillColor
export type PartialMcBimItem = (PostMcbimItemsApiResponse extends Array<infer T> ? T : never) & {
  searchHit?: boolean;
  fillColor?: string;
};

type PatchRoomArgs = {
  dispatch: AppDispatch;
  getState: () => IRootState;
  updatedRoom: IRoom;
};

export const patchRoomInCachedQueries = ({ dispatch, getState, updatedRoom }: PatchRoomArgs) => {
  if (!updatedRoom) return;

  const roomNumber = updatedRoom.roomNumber;
  const projectNo = updatedRoom.projectNo;

  const apiState: any = (getState() as IRootState).api;

  Object.values(apiState.queries).forEach((q: any) => {
    if (!q?.endpointName) return;

    // 1) Single room – match på projectNo + roomNumber
    if (
      q.endpointName === "getMultiplacerSingleRoom" &&
      q.originalArgs?.projectNo === projectNo &&
      q.originalArgs?.roomNumber === roomNumber
    ) {
      dispatch(
        openapi.util.updateQueryData("getMultiplacerSingleRoom", q.originalArgs, (draft: any) => {
          Object.assign(draft, updatedRoom); // mutera istället för return
        })
      );
    }

    // 2) Rooms-collection
    if (q.endpointName === "postMultiplacerRooms") {
      dispatch(
        openapi.util.updateQueryData("postMultiplacerRooms", q.originalArgs, (draft: any) => {
          if (draft?.[roomNumber]) {
            draft[roomNumber] = updatedRoom;
          }
        })
      );
    }
  });
};

export const patchRoomsInCachedQueries = ({
  dispatch,
  getState,
  updatedRooms,
}: {
  dispatch: AppDispatch;
  getState: () => IRootState;
  updatedRooms: IRoom[];
}) => {
  updatedRooms.forEach((updatedRoom) => {
    patchRoomInCachedQueries({ dispatch, getState, updatedRoom });
  });
};

// 🔹 Ny helper: tar queryFulfilled direkt
type PatchFromQueryFulfilledArgs = {
  dispatch: AppDispatch;
  getState: () => any;
  queryFulfilled: Promise<{ data: IRoom }>;
};

export const patchRoomFromQueryFulfilled = async ({
  dispatch,
  getState,
  queryFulfilled,
}: PatchFromQueryFulfilledArgs) => {
  try {
    const { data: updatedRoom } = await queryFulfilled;
    patchRoomInCachedQueries({ dispatch, getState, updatedRoom });
  } catch {
    // la RTK Query håndtere feilen som vanlig
  }
};

export const patchRoomsFromQueryFulfilled = async ({
  dispatch,
  getState,
  queryFulfilled,
}: {
  dispatch: AppDispatch;
  getState: () => any;
  queryFulfilled: Promise<{ data: { rooms: IRoom[] } }>;
}) => {
  try {
    const { data } = await queryFulfilled;
    patchRoomsInCachedQueries({ dispatch, getState, updatedRooms: data.rooms });
  } catch {
    // la RTK Query håndtere feilen som vanlig
  }
};
